// app/composables/season/useStandingsKeys.ts

import type { SeasonWebStandingsKeys } from "~~/src/season-cluster/domain/models/01.Season";
import type { Standing } from "~~/src/standing/domain/models/Standing";

interface UseStandingsKeys {
  getFeaturedStandingsKeys: (keys: SeasonWebStandingsKeys[] | undefined) => SeasonWebStandingsKeys[];
  getDisplayValue: (standing: Standing | any, column: SeasonWebStandingsKeys) => string | number;
  isFeatured: (column: SeasonWebStandingsKeys) => boolean;
  filterByPage: (keys: SeasonWebStandingsKeys[] | undefined, pageType: 'tables' | 'team') => SeasonWebStandingsKeys[];
}

export const useStandingsKeys = (): UseStandingsKeys => {
  /**
   * Filters and returns the first 3 standings keys marked as featured
   * @param keys - Array of SeasonWebStandingsKeys from metaInformation
   * @returns Array of up to 3 featured keys
   */
  function getFeaturedStandingsKeys(
    keys: SeasonWebStandingsKeys[] | undefined
  ): SeasonWebStandingsKeys[] {
    if (!keys || !Array.isArray(keys)) return [];

    const featured = keys.filter(key => key.featured === "true");
    return featured.slice(0, 3); // Limit to first 3 featured keys
  }

  /**
   * Checks if a column is marked as featured
   * @param column - SeasonWebStandingsKeys object
   * @returns true if featured === "true"
   */
  function isFeatured(column: SeasonWebStandingsKeys): boolean {
    return column.featured === "true";
  }

  /**
   * Filters standings keys by page type
   * Supports "tables", "team", or "tables,team" (comma-separated)
   * @param keys - Array of SeasonWebStandingsKeys
   * @param pageType - Page type to filter by ('tables' or 'team')
   * @returns Filtered array of keys that match the page type
   */
  function filterByPage(
    keys: SeasonWebStandingsKeys[] | undefined,
    pageType: 'tables' | 'team'
  ): SeasonWebStandingsKeys[] {
    if (!keys || !Array.isArray(keys)) return [];

    return keys.filter(key => {
      // If no page property, don't show it anywhere
      if (!key.page) return false;

      // Support both single value ("tables") and comma-separated ("tables,team")
      return key.page === pageType || key.page.includes(pageType);
    });
  }

  /**
   * Gets the display value for a standings column
   * Handles special cases: goalDifference calculation and concatKey concatenation
   * @param standing - Standing object or any object with standing properties
   * @param column - SeasonWebStandingsKeys object defining the column
   * @returns Display value (string for concatenated values, number for single values)
   */
  function getDisplayValue(standing: Standing | any, column: SeasonWebStandingsKeys): string | number {
    // Special case: goalDifference needs to be calculated
    if (column.key === "goalDifference") {
      const goalPro = standing.goalPro ?? 0;
      const goalAgainst = standing.goalAgainst ?? 0;

      // If there's a concatKey, we still need to handle it
      if (column.concatKey) {
        const concatValue = standing[column.concatKey as keyof typeof standing] ?? 0;
        return `${goalPro - goalAgainst} - ${concatValue}`;
      }

      return goalPro - goalAgainst;
    }

    // Get the main value
    const mainValue = standing[column.key as keyof typeof standing] ?? 0;

    // If there's a concatKey, concatenate the values
    if (column.concatKey) {
      const concatValue = standing[column.concatKey as keyof typeof standing] ?? 0;
      return `${mainValue} - ${concatValue}`;
    }

    // Return the simple value
    return mainValue;
  }

  return {
    getFeaturedStandingsKeys,
    getDisplayValue,
    isFeatured,
    filterByPage,
  };
};
